<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="$emit('close')" class="relative z-50">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" />
      </TransitionChild>

      <!-- Modal -->
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md bg-white overflow-hidden">
              <!-- Header -->
              <div class="bg-gray-900 px-6 py-4 flex items-center justify-between">
                <DialogTitle class="text-xl font-semibold text-white">
                  {{ $t('auth.login.title') }}
                </DialogTitle>
                <button
                  @click="$emit('close')"
                  class="text-white/80 hover:text-white transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Content -->
              <div class="p-8">
                <p class="text-gray-600 text-center mb-8">
                  {{ $t('auth.login.subtitle') }}
                </p>

                <!-- Loading State -->
                <div v-if="loading" class="text-center py-8">
                  <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto"></div>
                  <p class="mt-4 text-gray-600">{{ $t('common.loading') }}</p>
                </div>

                <!-- Error State -->
                <div v-else-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p class="text-red-600 text-sm text-center">{{ error }}</p>
                  <button @click="error = null" class="mt-2 text-sm text-red-600 underline w-full text-center">
                    {{ $t('common.try_again') }}
                  </button>
                </div>

                <!-- Google Sign In Button -->
                <div v-else id="google-signin-button" class="flex justify-center">
                  <!-- Google button will be rendered here -->
                </div>

                <p class="mt-6 text-xs text-gray-500 text-center">
                  {{ $t('auth.login.terms') }}
                </p>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'

interface Props {
  isOpen: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  close: []
  success: []
}>()

const authStore = useAuthStore()
const { t } = useI18n()
const loading = ref(false)
const error = ref<string | null>(null)

// Load Google Sign-In API when modal opens
watch(() => props.isOpen, async (isOpen) => {
  if (isOpen) {
    error.value = null
    await loadGoogleScript()
    renderGoogleButton()
  }
})

function loadGoogleScript(): Promise<void> {
  return new Promise((resolve) => {
    if (document.getElementById('google-signin-script')) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = 'google-signin-script'
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    document.head.appendChild(script)
  })
}

function renderGoogleButton() {
  // Wait for script to load and DOM to update
  setTimeout(() => {
    if (window.google && document.getElementById('google-signin-button')) {
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
      console.log('VITE_GOOGLE_CLIENT_ID:', clientId) // Debug log

      window.google.accounts.id.initialize({
        client_id: clientId || '564576819142-65a16dis5nqpj84f3j04tdr5ijn8o2kb.apps.googleusercontent.com',
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true
      })

      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: 'outline',
          size: 'large',
          width: 280,
          text: 'signin_with'
        }
      )
    }
  }, 100)
}

async function handleCredentialResponse(response: { credential: string }) {
  loading.value = true
  error.value = null

  try {
    const success = await authStore.loginWithGoogle(response.credential)

    if (success) {
      emit('success')
      emit('close')
    } else {
      error.value = authStore.error || 'Authentication failed'
    }
  } catch (err) {
    error.value = 'An error occurred during sign in'
  } finally {
    loading.value = false
  }
}
</script>

<script lang="ts">
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void
          renderButton: (element: HTMLElement | null, options: any) => void
        }
      }
    }
  }
}
</script>
